﻿import React, { useState, useEffect } from 'react';
import { BenchSheetBasicReading } from '../models/bench-sheet-basic-reading';
import BasicReadingsComponent from './BasicReadingsComponent';
import { CrudFunctions } from '../utilities/crud-functions';
import { Entities } from '../models/enums';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType } from '../models/enums';
import ItemBasicInformationDialog from './ItemBasicInformationDialog';
import { ItemBasicInformation } from '../models/item-basic-information';
import AuditHistoryComponent from './AuditHistoryComponent';

import DataGrid, {
    Column,
    Paging,
    Editing,
    Button as ColumnButton,
    Toolbar,
    Item as ToolbarItem,
    LoadPanel
} from 'devextreme-react/data-grid';

import { Popup as DialogPopup } from 'devextreme-react/popup';

const decimalFormat = {
    type: "fixedPoint",
    precision: 4
}

let key = 1;

export interface Props {
    benchSheetId?: string;
    spotCheckBenchSheetId?: string;
    isReadOnly: boolean;
    datagridHeight?: string;
    title?: string;
}

export default function BasicReadingsData(props: Props) {
    const [readings, setReadings] =
        useState<BenchSheetBasicReading[]>();

    const [selectedReading, setSelectedReading] =
        useState<BenchSheetBasicReading>();

    const [itemBasicInformation, setItemBasicInformation] =
        useState<ItemBasicInformation>();

    const [itemBasicInformationDialogVisible, setItemBasicInformationDialogVisible] =
        useState<boolean>();

    const [auditInformationDialogVisible, setAuditInformationDialogVisible] =
        useState<boolean>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    useEffect(() => {
        getReadings();
    }, [props.benchSheetId]);

    key++;

    return (
        <div>
            <div id="analysis-data-datagrid-container" className="pw-datagrid">
                <DataGrid
                    height={props.datagridHeight ? props.datagridHeight : "100%"}
                    keyExpr="readingId"
                    dataSource={readings ? readings : []}
                    rowAlternationEnabled={true}
                    onSelectionChanged={readingSelectionChanged}
                    onRowValidating={onReadingsDatagridRowValidating}
                    onRowRemoving={onReadingsRowRemoving}
                    showRowLines={true}
                    showBorders={true} >
                    <LoadPanel enabled={true} />
                    <Paging enabled={false} />

                    <Column type="buttons" width='8rem' >
                        <ColumnButton name="edit" />
                        <ColumnButton name="delete" />
                        <ColumnButton hint="audit information" icon="clock"
                            onClick={showAuditInformationDialog} />
                        <ColumnButton hint="information" icon="info"
                            onClick={onReadingInformationButtonClicked} />
                    </Column>

                    <Column dataField='sequence' dataType="number"
                        alignment='center' width='7rem' />
                    <Column dataField='sampleName' dataType="string" width={'15%'} />
                    <Column dataField='value' dataType="number" format={decimalFormat} width={'15%'} />
                    <Column />

                    <Toolbar>
                        <ToolbarItem location='before'>
                            <div className="pw-datagrid-toolbar-template">
                                <div className="pw-datagrid-toobar-title">{props.title ? props.title : "Readings Data"}</div>
                            </div>
                        </ToolbarItem>

                        <ToolbarItem name="addRowButton" location="after" />
                    </Toolbar>

                    <Editing newRowPosition="last"
                        mode="row"
                        useIcons={true}
                        allowAdding={true}
                        allowUpdating={true}
                        allowDeleting={true} />
                </DataGrid>

            </div>

            {auditInformationDialogVisible &&
                <DialogPopup visible={auditInformationDialogVisible}
                    onHiding={hideAuditInformationDialog}
                    title="Audit History"
                    minWidth={'50em'}
                    minHeight={'32em'}
                    dragEnabled={true}
                    resizeEnabled={true}>
                    <AuditHistoryComponent entityNumber={Entities.BenchSheetReadings}
                        key={`AuditHistory${key}`}
                        childData={false}
                        rowPointer={selectedReading && selectedReading.readingId ?
                            selectedReading.readingId : undefined} />
                </DialogPopup>
            }

            {itemBasicInformationDialogVisible &&
                <DialogPopup visible={itemBasicInformationDialogVisible}
                    onHiding={hideItemBasicInformationDialog}
                    title="Item Basic Information"
                    width={'25rem'}
                    height={'21rem'}
                    dragEnabled={false}
                    resizeEnabled={false}>
                    <ItemBasicInformationDialog initInformation={itemBasicInformation}
                        onOkClick={hideItemBasicInformationDialog}
                        key={`IBI${key}`} />
                </DialogPopup>
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }

        </div>
    );

    function onReadingInformationButtonClicked(e: any) {
        if (e.row && e.row.data) {
            setSelectedReading(e.row.data);
            showReadingInformationDialog(e.row.data);
        }
    }

    function readingSelectionChanged(e: any) {
        if (e.selectedRowsData && e.selectedRowsData.length > 0) {
            setSelectedReading(e.selectedRowsData[0]);
        }
    }

    async function onReadingsDatagridRowValidating(e: any) {
        //console.log("onGlasswareItemsDatagridRowValidating");
        //console.log(e);
        if (e.oldData) {
            let reading = e.oldData as BenchSheetBasicReading;

            if (reading) {
                let updatedReading = { ...reading, ...e.newData };

                updateReading(updatedReading)
                    .then(() => {
                        //showToast("Glassware Item updated", "success");
                        getReadings();
                    })
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Updating Reading");
                    });
            }
        }
        else {
            if (e.newData) {
                let reading = e.newData as BenchSheetBasicReading;

                if (reading) {
                    reading.benchSheetId = props.benchSheetId;

                    await insertReading(reading)
                        .then(() => {
                            //showToast("Glassware Item saved", "success");
                            getReadings();
                        })
                        .catch((res) => {
                            showMessage(res, ModalMessageType.Error, "Error Inserting Reading");
                        });
                }
            }
        }
    }

    // delete

    function onReadingsRowRemoving(e: any) {
        if (e.data && e.data.readingId)
            deleteReading(e.data.readingId)
                .then(() => {
                    //showToast("Glassware Item deleted", "success");
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Deleting Glassware Item");
                });
    }

    // CRUD functions

    async function getReadings() {
        const url = props.spotCheckBenchSheetId ? `${props.benchSheetId}/${props.spotCheckBenchSheetId}` : props.benchSheetId;

        await CrudFunctions.getData(`benchsheets/benchsheetbasicreadings/${url}`)
            .then((data) => {
                setReadings(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Readings");
            });
    }

    async function insertReading(reading: BenchSheetBasicReading) {
        return await CrudFunctions.postData("benchsheets/benchsheetbasicreading", reading)
    }

    async function updateReading(reading: BenchSheetBasicReading) {
        return await CrudFunctions.putData("benchsheets/benchsheetbasicreading", reading)
    }

    async function deleteReading(readingId: string) {
        return await CrudFunctions.deleteData(`benchsheets/reading/${readingId}`)
    }

    // show/hide

    function showReadingInformationDialog(reading: BenchSheetBasicReading) {
        if (reading) {
            let itemInformation: ItemBasicInformation = {
                rowPointer: reading.readingId,
                createTimestamp: reading.createTimestamp,
                lastModifiedTimestamp: reading.modifyTimestamp,
                createUser: reading.createUser,
                modifyUser: reading.modifyUser
            };

            setItemBasicInformation(itemInformation);
            setItemBasicInformationDialogVisible(true);
        }
    }

    function hideItemBasicInformationDialog() {
        setItemBasicInformationDialogVisible(false);
    }

    function showAuditInformationDialog() {
        setAuditInformationDialogVisible(true);
    }

    function hideAuditInformationDialog() {
        setAuditInformationDialogVisible(false);
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }
}


